import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    Typography,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Divider,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";


const FilterDialog = ({
    open,
    onClose,
    filterState,
    setFilterState,
    activeColumns,
    onApply,
    onClear,
}) => {
    const addFilterRule = () => {
        if (activeColumns.length === 0) return;
        setFilterState((prev) => ({
            ...prev,
            rules: [
                ...prev.rules,
                {
                    field: activeColumns.find((col) => col.key !== "__rowNumber")?.key || activeColumns[0].key,
                    operator: "=",
                    value: "",
                },
            ],
        }));
    };

    // Update a filter rule
    const updateFilterRule = (index, field, value) => {
        const newRules = [...filterState.rules];
        newRules[index] = { ...newRules[index], [field]: value };
        setFilterState({ ...filterState, rules: newRules });
    };

    // Get operators based on data type
    const getOperatorsForType = (type) => {
        switch (type) {
            case "number":
            case "date":
                return [
                    { value: "=", label: "=" },
                    { value: "!=", label: "!=" },
                    { value: ">", label: ">" },
                    { value: "<", label: "<" },
                    { value: ">=", label: ">=" },
                    { value: "<=", label: "<=" },
                ];
            case "string":
                return [
                    { value: "=", label: "=" },
                    { value: "!=", label: "!=" },
                    { value: "contains", label: "Contains" },
                    { value: "startsWith", label: "Starts with" },
                    { value: "endsWith", label: "Ends with" },
                ];
            case "boolean":
                return [
                    { value: "=", label: "=" },
                    { value: "!=", label: "!=" },
                ];
            default:
                return [
                    { value: "=", label: "=" },
                    { value: "!=", label: "!=" },
                ];
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
            <DialogTitle>
                Filter
            </DialogTitle>
            <Divider sx={{ m: 0 }} />
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={filterState.logicOperator}
                            onChange={(e, value) => value && setFilterState({ ...filterState, logicOperator: value })}
                        >
                            <ToggleButton value="AND" sx={{ minWidth: "50px" }}>
                                AND
                            </ToggleButton>
                            <ToggleButton value="OR" sx={{ minWidth: "50px" }}>
                                OR
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Button 
                            variant="outlined" 
                            size="small" 
                            startIcon={<AddIcon />} 
                            onClick={addFilterRule}
                        >
                            Add Rule
                        </Button>
                    </Box>
                    {filterState.rules.map((rule, index) => {
                        const column = activeColumns.find((col) => col.key === rule.field);
                        const dataType = column?.type || "string";
                        const operators = getOperatorsForType(dataType);

                        return (
                            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: '30%' }}>
                                    <InputLabel>Field</InputLabel>
                                    <Select
                                        value={rule.field}
                                        onChange={(e) => updateFilterRule(index, "field", e.target.value)}
                                        label="Field"
                                    >
                                        {activeColumns
                                            .filter((col) => col.key !== "__rowNumber")
                                            .map((column) => (
                                                <MenuItem key={column.key} value={column.key}>
                                                    {column.label}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: '20%' }}>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        value={rule.operator}
                                        onChange={(e) => updateFilterRule(index, "operator", e.target.value)}
                                        label="Operator"
                                    >
                                        {operators.map((op) => (
                                            <MenuItem key={op.value} value={op.value}>
                                                {op.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    value={rule.value}
                                    onChange={(e) => updateFilterRule(index, "value", e.target.value)}
                                    label="Value"
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    onClick={() => {
                                        const newRules = [...filterState.rules];
                                        newRules.splice(index, 1);
                                        setFilterState({ ...filterState, rules: newRules });
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        );
                    })}
                    {filterState.rules.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
                            No filter rules. Click "Rule" to add one.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClear}
                >
                    Clear
                </Button>
                <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={onApply}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterDialog;